import { Label } from "@mdotm/shadcn/mdotm/components";
import { CircularProgressBar, Text } from "@mdotm/shadcn/legacy/components";
import { useUniqueDOMId } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { switchExpr } from "@mdotm/shadcn/legacy/utils";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocaleFormatters } from "$root/localization/hooks";
import type { MarketScenarioWithMdotmCommentary } from "./MarketViewPositioning.types";
import type { MarketViewSettings } from "@eu.mdotm.api/sphere";

export function MarketViewSummary(props: {
	marketScenario?: MarketScenarioWithMdotmCommentary;
	marketViewSettings?: MarketViewSettings;
	scenarioLabel: string | ReactNode;
	loading?: boolean;
	selectedScenarioId: string | null;
}): React.JSX.Element {
	const typeLabelId = useUniqueDOMId();
	const scenarioLabelId = useUniqueDOMId();
	const forecastLabelId = useUniqueDOMId();
	const fromLabelId = useUniqueDOMId();
	const toLabelId = useUniqueDOMId();
	const { t } = useTranslation();
	const { formatDate } = useLocaleFormatters();

	return (
		<div className="flex flex-row pb-8 @5xl:[&>*:nth-child(even)]:mx-4">
			{typeof props.scenarioLabel === "string" ? (
				<div>
					<Label htmlFor={scenarioLabelId}>Market scenario</Label>
					<Text as="div" id={scenarioLabelId} type="Body/L/Bold" className="py-1">
						{props.scenarioLabel}
					</Text>
				</div>
			) : (
				props.scenarioLabel
			)}
			<div className="mx-2 h-12 self-center border-r" style={{ borderColor: themeCSSVars.palette_N100 }} />
			<div className="min-w-[120px] flex flex-col">
				<Label htmlFor={typeLabelId}>Type</Label>
				{!props.loading ? (
					<Text as="div" id={typeLabelId} type="Body/L/Bold" className="py-1">
						{props.marketViewSettings
							? props.marketViewSettings.customMarketViewName
							: props.marketScenario?.marketViewType
								? switchExpr(props.marketScenario.marketViewType, {
										EXPECTED_RETURNS_VOLATILITY: () => "Expected returns",
										POSITIONING_INDICATORS: () => "Under/over weight",
									})
								: "-"}
					</Text>
				) : (
					<CircularProgressBar value="indeterminate" className="w-4 h-4 mt-auto mb-1" />
				)}
			</div>
			{/* {props.marketScenario?.forecastHorizon && ( */}

			<div className="mx-2 h-12 self-center border-r" style={{ borderColor: themeCSSVars.palette_N100 }} />
			<div className="flex flex-col">
				<Label htmlFor={forecastLabelId}>{t("TABLE.HEADERS.FORECAST_HORIZON")}</Label>
				{props.marketScenario?.forecastHorizon && !props.loading ? (
					props.marketScenario.marketViewType === "EXPECTED_RETURNS_VOLATILITY" ? (
						<Text as="div" id={forecastLabelId} type="Body/L/Bold" className="py-1">
							{t(`INVESTMENT_HORIZON.${props.marketScenario.forecastHorizon}`) ?? "-"}
						</Text>
					) : (
						<Text as="div" id={forecastLabelId} type="Body/L/Bold" className="py-1">
							{props.selectedScenarioId === "SPHERE_FORECAST_POSITIONING_ONE_MONTH"
								? "Tactical"
								: (t(`POSITIONING_INVESTMENT_HORIZON.${props.marketScenario.forecastHorizon}`) ?? "-")}
						</Text>
					)
				) : (
					<CircularProgressBar value="indeterminate" className="w-4 h-4 mt-auto mb-1" />
				)}
			</div>

			{/* )} */}

			<div className="mx-2 h-12 self-center border-r" style={{ borderColor: themeCSSVars.palette_N100 }} />
			<div className="flex flex-col">
				<Label htmlFor={fromLabelId}>{t("TABLE.HEADERS.CREATION")}</Label>
				{props.marketScenario?.historicalInvestmentHorizon?.from && !props.loading ? (
					<Text as="div" id={fromLabelId} type="Body/L/Bold" className="py-1">
						{formatDate(props.marketScenario.historicalInvestmentHorizon.from)}
					</Text>
				) : (
					<CircularProgressBar value="indeterminate" className="w-4 h-4 mt-auto mb-1" />
				)}
			</div>
			<div className="mx-2 h-12 self-center border-r" style={{ borderColor: themeCSSVars.palette_N100 }} />
			<div className="flex flex-col">
				<Label htmlFor={toLabelId}>{t("TABLE.HEADERS.EXPIRATION")}</Label>
				{props.marketScenario?.historicalInvestmentHorizon?.to && !props.loading ? (
					<Text as="div" id={toLabelId} type="Body/L/Bold" className="py-1">
						{formatDate(props.marketScenario.historicalInvestmentHorizon.to)}
					</Text>
				) : (
					<CircularProgressBar value="indeterminate" className="w-4 h-4 mt-auto mb-1" />
				)}
			</div>
		</div>
	);
}
